/**
 * Browser errors to Sentry (project `wbm-web`), for the SPA and the share page's island.
 * Before this, a dead page or a silent audio failure on a real phone went nowhere: Sentry
 * had only the backend and the agent, and the most a lane could say was "dev saw no
 * events from that device".
 *
 * Errors and unhandled rejections ONLY. No session replay, no tracing, no breadcrumbs
 * (a breadcrumb can carry a kid's typed or spoken text), no user (only a placeholder IP,
 * so Sentry places the device nowhere), no cookies, no query strings, IP storage off on
 * the project. What an event keeps: the error's type, a scrubbed message, the stack,
 * the page path (ids only), the User-Agent, and our tags (release = build sha, surface,
 * platform). The COPPA posture is the backend's
 * (`backend/app/telemetry.py`): send nothing that identifies a child.
 *
 * The plan's 5,000 errors a month are shared with wbm-backend and wbm-agent, and the
 * free plan cannot cap a project's key, so the cap is here (`SendBudget`): a page sends
 * at most `PAGE_BUDGET` events, a device at most `DEVICE_BUDGET_PER_HOUR` an hour.
 * Spike protection is on for the project as the org-wide backstop.
 *
 * No DSN means a complete no-op (local, CI, any build without `VITE_SENTRY_DSN`), the
 * backend's convention. The share island imports this module: it must stay free of
 * `@capacitor/*` and of `platform/` (guarded by `errorReporting.test.ts`).
 */

import {
  dedupeIntegration,
  globalHandlersIntegration,
  httpContextIntegration,
  init,
  linkedErrorsIntegration,
  type ErrorEvent,
} from "@sentry/browser";

export type ErrorSurface = "spa" | "share";
export type ErrorPlatform = "web" | "ios" | "android";

export interface ErrorReportingOptions {
  surface: ErrorSurface;
  platform: ErrorPlatform;
}

export interface ErrorReportingEnv {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_SAMPLE_RATE?: string;
  VITE_BUILD_SHA?: string;
}

export const PAGE_BUDGET = 10;
export const DEVICE_BUDGET_PER_HOUR = 20;
const DEVICE_BUDGET_KEY = "wbm.errorReportBudget";
const MESSAGE_MAX = 300;

// Our own origins, plus the native shell's. Anything else (an extension, a third-party
// script) is noise that would only spend the shared quota.
const ALLOW_URLS = [
  /^https:\/\/([a-z0-9-]+\.)?worldby\.me\//,
  /^capacitor:\/\/localhost\//,
  /^https?:\/\/localhost(:\d+)?\//,
];

/** A page-load and per-device-hour send cap. Storage failures fall back to the page cap. */
export class SendBudget {
  private sent = 0;

  constructor(
    private readonly storage: () => Storage | null = () => window.localStorage,
    private readonly now: () => number = Date.now,
  ) {}

  take(): boolean {
    if (this.sent >= PAGE_BUDGET) return false;
    const hour = Math.floor(this.now() / 3_600_000);
    try {
      const store = this.storage();
      if (store) {
        const [h, n] = (store.getItem(DEVICE_BUDGET_KEY) ?? "").split(":").map(Number);
        const used = h === hour && Number.isFinite(n) ? n : 0;
        if (used >= DEVICE_BUDGET_PER_HOUR) return false;
        store.setItem(DEVICE_BUDGET_KEY, `${hour}:${used + 1}`);
      }
    } catch {
      // Private mode or blocked storage: the page cap still holds.
    }
    this.sent += 1;
    return true;
  }
}

/**
 * Quoted spans are where a browser echoes data into a message (Chromium's JSON.parse
 * error quotes the input it choked on), so any of four or more characters goes. A
 * non-Error rejection's value is replaced by its kind.
 */
export function scrubMessage(message: string): string {
  let out = message.replace(
    /^(Non-Error promise rejection captured with value:|Object captured as promise rejection with keys:).*$/s,
    "$1 [redacted]",
  );
  out = out.replace(/"[^"\n]{4,}"|'[^'\n]{4,}'|`[^`\n]{4,}`/g, '"[redacted]"');
  out = out.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]");
  return out.length > MESSAGE_MAX ? `${out.slice(0, MESSAGE_MAX)}…` : out;
}

function stripQuery(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

// Without an IP on the event, Sentry geolocates the connection's address to a city
// before IP scrubbing drops it (seen 2026-10-27: "Leander, US" on a local test event;
// an explicit null does not stop it). A fixed unroutable address resolves to no place.
const NO_IP = "0.0.0.0";

/** Pure: the event as it may leave the device. */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  event.user = { ip_address: NO_IP };
  delete event.extra;
  delete event.breadcrumbs;
  if (event.request) {
    const ua = event.request.headers?.["User-Agent"];
    event.request = {
      url: event.request.url ? stripQuery(event.request.url) : undefined,
      headers: ua ? { "User-Agent": ua } : undefined,
    };
  }
  if (typeof event.message === "string") event.message = scrubMessage(event.message);
  for (const ex of event.exception?.values ?? []) {
    if (typeof ex.value === "string") ex.value = scrubMessage(ex.value);
    for (const frame of ex.stacktrace?.frames ?? []) {
      if (frame.filename) frame.filename = stripQuery(frame.filename);
      if (frame.abs_path) frame.abs_path = stripQuery(frame.abs_path);
      delete frame.vars;
    }
  }
  return event;
}

// Each read by its literal name: the share island's build (web/astro.config.mjs) fills
// these through `define`, which replaces `import.meta.env.X` expressions, not the object.
function buildEnv(): ErrorReportingEnv {
  return {
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    VITE_SENTRY_SAMPLE_RATE: import.meta.env.VITE_SENTRY_SAMPLE_RATE,
    VITE_BUILD_SHA: import.meta.env.VITE_BUILD_SHA,
  };
}

let installed = false;

/** Start reporting. Returns false (and does nothing) without a DSN or on a second call. */
export function installErrorReporting(
  opts: ErrorReportingOptions,
  env: ErrorReportingEnv = buildEnv(),
  budget: SendBudget = new SendBudget(),
): boolean {
  const dsn = env.VITE_SENTRY_DSN?.trim();
  if (!dsn || installed) return false;
  installed = true;
  const environment = env.VITE_SENTRY_ENVIRONMENT || "production";
  const rate = Number(env.VITE_SENTRY_SAMPLE_RATE);
  init({
    dsn,
    environment,
    release: env.VITE_BUILD_SHA || undefined,
    sampleRate: rate > 0 && rate <= 1 ? rate : 1,
    sendDefaultPii: false,
    defaultIntegrations: false,
    integrations: [
      globalHandlersIntegration(),
      linkedErrorsIntegration(),
      dedupeIntegration(),
      httpContextIntegration(),
    ],
    maxBreadcrumbs: 0,
    allowUrls: ALLOW_URLS,
    initialScope: { tags: { surface: opts.surface, platform: opts.platform } },
    beforeSend: (event) => (budget.take() ? scrubEvent(event) : null),
  });
  armTestSwitch(environment);
  return true;
}

/**
 * Dev's check that a real device reports: `?wbm_sentry_test=error` throws, `=rejection`
 * rejects, a moment after load. Off in production.
 */
function armTestSwitch(environment: string): void {
  if (environment === "production" || typeof window === "undefined") return;
  const kind = new URLSearchParams(window.location.search).get("wbm_sentry_test");
  if (kind === "error") {
    setTimeout(() => {
      throw new Error("wbm_sentry_test: thrown error");
    }, 500);
  } else if (kind === "rejection") {
    setTimeout(() => void Promise.reject(new Error("wbm_sentry_test: unhandled rejection")), 500);
  }
}

/** Test seam. */
export function _resetErrorReportingForTests(): void {
  installed = false;
}
